import {
  Clipboard,
  Clock3,
  ExternalLink,
  Eye,
  EyeOff,
  History,
  KeyRound,
  LockKeyhole,
  Pencil,
  Plus,
  ShieldAlert,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useApp } from "../../domain/AppContext";
import {
  Button,
  Dialog,
  EmptyState,
  InlineMessage,
  PageHeader,
  SearchField,
} from "../../components/ui";

const readCredentialSessionLimit = () => 15 * 60;

export function CredentialsPage() {
  const { profile, data, dataLoading, refresh, revealCredential, createCredential, updateCredential, notify } = useApp();
  const [query, setQuery] = useState("");
  const [revealed, setRevealed] = useState<Record<string, { secret: string; expires: number }>>({});
  const unitRef = useRef(profile?.unitId);
  unitRef.current = profile?.unitId;
  useEffect(() => { setRevealed({}); }, [profile?.unitId]);
  const [credentialSessionLimit] = useState(readCredentialSessionLimit);
  const [credentialSessionSeconds, setCredentialSessionSeconds] = useState(readCredentialSessionLimit);
  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [credentialForm, setCredentialForm] = useState({ platform: '', olympiadId: '', url: '', username: '', secret: '', owner: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const timer = window.setInterval(() => {
      const now = Date.now();
      setRevealed((current) =>
        Object.fromEntries(
          Object.entries(current).filter(([, entry]) => entry.expires > now),
        ),
      );
        setCredentialSessionSeconds((current) => {
        if (current <= 1) {
          setRevealed({});
          return 0;
        }
        return current - 1;
      });
    }, 1000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    const renew = () => setCredentialSessionSeconds(credentialSessionLimit);
    window.addEventListener("pointerdown", renew);
    window.addEventListener("keydown", renew);
    return () => {
      window.removeEventListener("pointerdown", renew);
      window.removeEventListener("keydown", renew);
    };
  }, [credentialSessionLimit]);

  const filtered = useMemo(
    () =>
      (data?.credentials ?? []).filter((item) =>
        `${item.platform} ${item.username} ${item.owner}`
          .toLocaleLowerCase("pt-BR")
          .includes(query.toLocaleLowerCase("pt-BR")),
      ),
    [data?.credentials, query],
  );

  if (!profile || !data) return null;
  if (data.credentialsError) return <div className="credentials-page">
    <PageHeader eyebrow="Acesso restrito" title="Credenciais" description="Visualização protegida para coordenadores e gestão completa para o Gestor de Sistema." />
    <InlineMessage type="error" title="Não foi possível carregar as credenciais">{data.credentialsError}</InlineMessage>
    <Button loading={dataLoading} onClick={() => void refresh()}>Tentar novamente</Button>
  </div>;
  const canManage = Boolean(profile.isSystemManager);

  const reveal = async (id: string) => {
    const unit = unitRef.current;
    const result = await revealCredential(id);
    if (unitRef.current !== unit) return;
    if (result.ok) {
      setRevealed((current) => ({ ...current, [id]: { secret: result.data.secret, expires: Date.now() + 30_000 } }));
      setCredentialSessionSeconds(credentialSessionLimit);
      notify({
        type: "success",
        title: "Credencial revelada por 30 segundos",
        description: "O acesso foi registrado em Atividades.",
      });
    } else notify({ type: "error", title: result.error.message });
  };

  const copy = async (value: string, label: string) => {
    await navigator.clipboard.writeText(value);
    notify({
      type: "success",
      title: `${label} copiado`,
      description:
        "A área de transferência deve ser limpa após o uso em produção.",
    });
  };
  const saveCredential = async () => {
    if (!credentialForm.platform.trim() || !credentialForm.username.trim() || !credentialForm.url.trim()) {
      notify({ type: 'error', title: 'Preencha os dados da credencial' });
      return;
    }
    if (editingId && !credentialForm.secret.trim()) {
      notify({ type: 'error', title: 'Informe a nova senha para atualizar a credencial' });
      return;
    }
    setSaving(true);
    const payload = { ...credentialForm, unitId: profile.unitId, olympiadId: credentialForm.olympiadId || data.olympiads[0]?.id };
    const result = editingId ? await updateCredential(editingId, payload) : await createCredential(payload);
    setSaving(false);
    if (!result.ok) return notify({ type: 'error', title: editingId ? 'Credencial não atualizada' : 'Credencial não cadastrada', description: result.error.message });
    setFormOpen(false); setEditingId(null); setCredentialForm({ platform: '', olympiadId: '', url: '', username: '', secret: '', owner: '' });
    notify({ type: 'success', title: editingId ? 'Credencial atualizada' : 'Credencial cadastrada', description: 'A alteração foi registrada com segurança.' });
  };
  const openCreate = () => {
    setEditingId(null);
    setCredentialForm({ platform: '', olympiadId: '', url: '', username: '', secret: '', owner: '' });
    setFormOpen(true);
  };
  const openEdit = (credential: (typeof data.credentials)[number]) => {
    setEditingId(credential.id);
    setCredentialForm({ platform: credential.platform, olympiadId: credential.olympiadId, url: credential.url, username: credential.username, secret: '', owner: credential.owner });
    setFormOpen(true);
  };

  return (
    <div className="credentials-page">
      <PageHeader
        eyebrow="Acesso restrito"
        title="Credenciais"
        description="Visualização protegida para coordenadores e gestão completa para o Gestor de Sistema."
        actions={canManage ? <Button onClick={openCreate}>
            <Plus size={17} /> Nova credencial
          </Button> : undefined}
      />
      <InlineMessage title="Credenciais protegidas">
        <p>
          Os segredos ficam armazenados de forma criptografada no Supabase e
          são liberados por tempo limitado, com registro de cada acesso.
        </p>
      </InlineMessage>
      <div className="credential-session">
        <LockKeyhole size={20} />
        <div>
          <strong>Sessão protegida</strong>
          <p>A exibição de senhas expira após 15 minutos de inatividade.</p>
        </div>
        <span>
          <Clock3 size={16} /> {String(Math.floor(credentialSessionSeconds / 60)).padStart(2, "0")}:{String(credentialSessionSeconds % 60).padStart(2, "0")} restantes
        </span>
      </div>
      <div className="admin-filter-bar">
        <SearchField
          value={query}
          onChange={setQuery}
          placeholder="Buscar plataforma ou olimpíada..."
        />
        <Button
          variant="secondary"
          onClick={() => (document.location.hash = "#/atividades")}
        >
          <History size={17} /> Histórico de acessos
        </Button>
      </div>
      {filtered.length ? (
        <div className="credential-list">
          {filtered.map((credential) => {
            const visible = Boolean(revealed[credential.id]);
            const olympiad = data.olympiads.find(
              (item) => item.id === credential.olympiadId,
            );
            return (
              <article key={credential.id}>
                <div className="credential-platform">
                  <span>
                    <KeyRound size={19} />
                  </span>
                  <div>
                    <strong>{credential.platform}</strong>
                    <p>
                      {olympiad?.acronym ? `${olympiad.acronym} · ` : ''}Acesso institucional · {credential.owner}
                    </p>
                  </div>
                </div>
                <div className="credential-field">
                  <span>Usuário</span>
                  <code>{credential.username}</code>
                  <button
                    type="button"
                    aria-label="Copiar usuário"
                    onClick={() => copy(credential.username, "Usuário")}
                  >
                    <Clipboard size={16} />
                  </button>
                </div>
                <div className="credential-field secret">
                  <span>Senha</span>
                  <code>{visible ? revealed[credential.id].secret : "••••••••••"}</code>
                  {visible ? (
                    <em>
                      {Math.max(
                        1,
                        Math.ceil(
                          (revealed[credential.id].expires - Date.now()) / 1000,
                        ),
                      )}
                      s
                    </em>
                  ) : null}
                  <button
                    type="button"
                    aria-label={visible ? "Ocultar senha" : "Revelar senha"}
                    onClick={() =>
                      visible
                        ? setRevealed((current) => {
                            const next = { ...current };
                            delete next[credential.id];
                            return next;
                          })
                        : reveal(credential.id)
                    }
                  >
                    {visible ? <EyeOff size={17} /> : <Eye size={17} />}
                  </button>
                  {visible ? (
                    <button
                      type="button"
                      aria-label="Copiar senha"
                      onClick={() => copy(revealed[credential.id].secret, "Senha")}
                    >
                      <Clipboard size={16} />
                    </button>
                  ) : null}
                </div>
                <a
                  href={credential.url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Abrir portal <ExternalLink size={16} />
                </a>
                <small>
                  Atualizada em{" "}
                  {credential.updatedAt.split("-").reverse().join("/")}
                </small>
                {canManage ? <Button variant="ghost" onClick={() => openEdit(credential)}><Pencil size={15} /> Editar</Button> : null}
              </article>
            );
          })}
        </div>
      ) : (
        <EmptyState
          icon={<ShieldAlert />}
          title="Nenhuma credencial encontrada"
          description="A busca não encontrou plataformas acessíveis ao seu perfil."
        />
      )}
      <Dialog
        open={formOpen}
        title={editingId ? "Editar credencial" : "Cadastrar credencial"}
        description={editingId ? "Atualize os dados desta credencial e informe a nova senha." : "Cadastre credenciais institucionais autorizadas para uso da equipe."}
        onClose={() => { setFormOpen(false); setEditingId(null); }}
        actions={
          <>
            <Button variant="ghost" onClick={() => { setFormOpen(false); setEditingId(null); }}>
              Cancelar
            </Button>
            <Button loading={saving} onClick={() => void saveCredential()}>
              {editingId ? 'Salvar alterações' : 'Salvar credencial'}
            </Button>
          </>
        }
      >
        <div className="dialog-form">
          <label>
            Nome da plataforma
            <input value={credentialForm.platform} onChange={(event) => setCredentialForm({ ...credentialForm, platform: event.target.value })} placeholder="Portal da olimpíada" />
          </label>
          <label>
            Olimpíada
            <select value={credentialForm.olympiadId || data.olympiads[0]?.id} onChange={(event) => setCredentialForm({ ...credentialForm, olympiadId: event.target.value })}>
              {data.olympiads.map((item) => (
                <option key={item.id} value={item.id}>{item.acronym} 2026</option>
              ))}
            </select>
          </label>
          <label>
            URL de acesso
            <input type="url" value={credentialForm.url} onChange={(event) => setCredentialForm({ ...credentialForm, url: event.target.value })} placeholder="https://portal.institucional.br/" />
          </label>
          <div className="form-grid two">
            <label>
              Usuário
              <input value={credentialForm.username} onChange={(event) => setCredentialForm({ ...credentialForm, username: event.target.value })} />
            </label>
            <label>
              Senha
              <input type="password" value={credentialForm.secret} onChange={(event) => setCredentialForm({ ...credentialForm, secret: event.target.value })} />
            </label>
          </div>
          <InlineMessage title="Exposição mínima">
            Informe somente credenciais institucionais autorizadas. Segredos não devem ser compartilhados fora da plataforma.
          </InlineMessage>
        </div>
      </Dialog>
    </div>
  );
}
